import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { fetchSubscriptionComponents } from '@/utils/services/componentService';
import { useOrganization } from '@/context/OrganizationContext';
import { useAuth } from '@/context/AuthContext';
import { initialNodes, NodeData } from './initial-nodes';
import { initialEdges } from './initial-edges';
import { generateUUID, nodeTypes } from './utils/diagramUtils';
import { useDiagramSave } from './hooks/useDiagramSave';
import { useDiagramHistory } from './hooks/useDiagramHistory';
import { useDiagramLoad } from './hooks/useDiagramLoad';
import { useDiagramAutoSave } from './hooks/useDiagramAutoSave';
import { toast } from 'sonner';
import {
  Edge,
  Node,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  MarkerType,
} from '@xyflow/react';
import { Component, ComponentType } from '@/utils/types/componentTypes';
import { DiagramEditorArea } from "./DiagramEditorArea";
import { EditComponentDialog } from "@/components/components/EditComponentDialog";
import { CreateComponentDialog } from "@/components/components/CreateComponentDialog";
import { ComponentTypeDialog } from "@/components/component-types/ComponentTypeDialog";
import { useComponentManagement } from "@/hooks/useComponentManagement";
import { createComponent } from "@/utils/services/componentCrudService";
import { fetchComponentTypes, createComponentType } from "@/utils/services/componentTypeService";
import { DiagramContextMenu } from "./DiagramContextMenu";
import { DiagramPropertiesPanels } from "./DiagramPropertiesPanels";
import { useDiagramContextMenu } from "./hooks/useDiagramContextMenu";
import { useDiagramPanels } from "./hooks/useDiagramPanels";
import { useDiagramHistorySync } from "./hooks/useDiagramHistorySync";
import { useDiagramMultiSelection } from "./hooks/useDiagramMultiSelection";

export function DiagramEditor() {
  const { id } = useParams();
  const location = useLocation();
  const { currentOrganization } = useOrganization();
  const { subscriptionData } = useAuth();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
  const [components, setComponents] = useState<Component[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDebugMode, setIsDebugMode] = useState(false);

  // Estados para edição de componente
  const [editComponentDialogOpen, setEditComponentDialogOpen] = useState(false);
  const [currentEditingComponent, setCurrentEditingComponent] = useState<Component | null>(null);
  const [isComponentSaving, setIsComponentSaving] = useState(false);
  
  // Estados para criação de novo componente
  const [createComponentDialogOpen, setCreateComponentDialogOpen] = useState(false);
  const [createTypeDialogOpen, setCreateTypeDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newlyCreatedComponent, setNewlyCreatedComponent] = useState<Component | null>(null);
  
  // Hook de gerenciamento de componentes para acessar componentTypes
  const { componentTypes } = useComponentManagement();

  // Text properties state
  const [selectedText, setSelectedText] = useState<Node<NodeData> | null>(null);
  const [showTextPanel, setShowTextPanel] = useState(false);

  // Image state
  const [selectedImage, setSelectedImage] = useState<Node<NodeData> | null>(null);

  const {
    selectedElements,
    toggleNodeSelection,
    toggleEdgeSelection,
    setMultipleSelection,
    clearSelection,
    hasMultipleSelection,
    hasSingleSelection,
    getSingleSelected
  } = useDiagramMultiSelection();

  const {
    contextMenuPosition,
    contextTargetType,
    contextTargetNode,
    contextTargetEdge,
    handleContextMenuNode,
    handleContextMenuEdge,
    closeContextMenu,
    setContextTargetType
  } = useDiagramContextMenu();

  const {
    selectedNode,
    showNodePanel,
    openNodePanel,
    closeNodePanel,
    selectedEdge,
    showEdgePanel,
    openEdgePanel,
    closeEdgePanel,
    selectedGroup,
    showGroupPanel,
    openGroupPanel,
    closeGroupPanel,
    selectedActor,
    showActorPanel,
    openActorPanel,
    closeActorPanel,
    setSelectedNode,
    setSelectedEdge,
    setSelectedGroup,
    setSelectedActor
  } = useDiagramPanels();

  const { handleSave } = useDiagramSave(id);
  const { history, historyIndex, handleUndo, handleRedo, addToHistory, setHistory, setHistoryIndex, isApplyingHistoryRef } = useDiagramHistory();
  
  // Carregar o diagrama primeiro
  const { diagramName, diagramLoaded: hookDiagramLoaded, isLoading: diagramIsLoading } = useDiagramLoad(id, setNodes, setEdges, setHistory, setHistoryIndex);

  // Só iniciar o sync do histórico DEPOIS que o diagrama foi carregado
  const shouldSyncHistory = hookDiagramLoaded && !diagramIsLoading;
  useDiagramHistorySync(shouldSyncHistory ? nodes : [], shouldSyncHistory ? edges : [], shouldSyncHistory ? addToHistory : () => {}, isApplyingHistoryRef);

  // Autosave com debounce de 3 segundos
  const { markAsSaved, ...autoSaveStatus } = useDiagramAutoSave(
    nodes,
    edges,
    handleSave,
    isApplyingHistoryRef,
    hookDiagramLoaded,
    id
  );

  // Calculate canUndo and canRedo based on history state
  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const openNodePropertiesPanel = useCallback(() => {
    if (contextTargetNode) {
      openNodePanel(contextTargetNode);
      setContextTargetType(null);
    }
  }, [contextTargetNode, openNodePanel, setContextTargetType]);

  const openEdgePropertiesPanel = useCallback(() => {
    if (contextTargetEdge) {
      openEdgePanel(contextTargetEdge);
      setContextTargetType(null);
    }
  }, [contextTargetEdge, openEdgePanel, setContextTargetType]);

  const openGroupPropertiesPanel = useCallback(() => {
    if (contextTargetNode) {
      openGroupPanel(contextTargetNode);
      setContextTargetType(null);
    }
  }, [contextTargetNode, openGroupPanel, setContextTargetType]);

  const openTextPropertiesPanel = useCallback(() => {
    if (contextTargetNode) {
      setSelectedText(contextTargetNode);
      setShowTextPanel(true);
      setContextTargetType(null);
    }
  }, [contextTargetNode, setContextTargetType]);

  const closeTextPropertiesPanel = useCallback(() => {
    setShowTextPanel(false);
    setTimeout(() => {
      setSelectedText(null);
    }, 100);
  }, []);

  const openActorPropertiesPanel = useCallback(() => {
    if (contextTargetNode) {
      openActorPanel(contextTargetNode);
      setContextTargetType(null);
    }
  }, [contextTargetNode, openActorPanel, setContextTargetType]);

  const handleDeleteContextTarget = () => {
    if (contextTargetType === 'node' || contextTargetType === 'group' || contextTargetType === 'text' || contextTargetType === 'actor' || contextTargetType === 'image') {
      if (contextTargetNode) {
        setNodes((nds) => nds.filter((node) => node.id !== contextTargetNode?.id));
        setSelectedNode(null);
        setSelectedGroup(null);
        setSelectedText(null);
        setSelectedActor(null);
        setSelectedImage(null);
        closeNodePanel();
        closeGroupPanel();
        closeTextPropertiesPanel();
        closeActorPanel();
      }
    } else if (contextTargetType === 'edge') {
      if (contextTargetEdge) {
        setEdges((eds) => eds.filter((edge) => edge.id !== contextTargetEdge?.id));
        setSelectedEdge(null);
        closeEdgePanel();
      }
    }
    setContextTargetType(null);
  };

  const handleSendToBack = () => {
    if (contextTargetNode) {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === contextTargetNode.id) {
            const currentZIndex = typeof node.style?.zIndex === 'number' ? node.style.zIndex : 0;
            return {
              ...node,
              style: {
                ...node.style,
                zIndex: currentZIndex - 1,
              },
            };
          }
          return node;
        })
      );
    }
    setContextTargetType(null);
  };

  const handleBringToFront = () => {
    if (contextTargetNode) {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === contextTargetNode.id) {
            const currentZIndex = typeof node.style?.zIndex === 'number' ? node.style.zIndex : 0;
            return {
              ...node,
              style: {
                ...node.style,
                zIndex: currentZIndex + 1,
              },
            };
          }
          return node;
        })
      );
    }
    setContextTargetType(null);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Delete') {
        if (selectedNode) {
          setNodes((nds) => nds.filter((node) => node.id !== selectedNode.id));
          closeNodePanel();
          toast.success("Elemento removido");
        } else if (selectedGroup) {
          setNodes((nds) => nds.filter((node) => node.id !== selectedGroup.id));
          closeGroupPanel();
          toast.success("Moldura removida");
        } else if (selectedText) {
          setNodes((nds) => nds.filter((node) => node.id !== selectedText.id));
          closeTextPropertiesPanel();
          toast.success("Texto removido");
        } else if (selectedActor) {
          setNodes((nds) => nds.filter((node) => node.id !== selectedActor.id));
          closeActorPanel();
          toast.success("Ator removido");
        } else if (selectedImage) {
          setNodes((nds) => nds.filter((node) => node.id !== selectedImage.id));
          setSelectedImage(null);
          toast.success("Imagem removida");
        } else if (selectedEdge) {
          setEdges((eds) => eds.filter((edge) => edge.id !== selectedEdge.id));
          setSelectedEdge(null);
          closeEdgePanel();
          toast.success("Conexão removida");
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNode, selectedGroup, selectedText, selectedActor, selectedImage, selectedEdge, setNodes, setEdges, closeNodePanel, closeGroupPanel, closeTextPropertiesPanel, closeActorPanel, closeEdgePanel, setSelectedEdge]);

  // Listener para evento de colar imagem (Ctrl+V / Cmd+V)
  useEffect(() => {
    const handlePaste = async (event: ClipboardEvent) => {
      // Verificar se há imagem no clipboard
      const items = event.clipboardData?.items;
      if (!items) return;
      
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          event.preventDefault();
          
          const file = items[i].getAsFile();
          if (!file) continue;
          
          // Converter para base64
          const reader = new FileReader();
          reader.onload = (e) => {
            const imageData = e.target?.result as string;
            
            if (!imageData || !reactFlowInstance) return;
            
            // Calcular posição central do viewport
            const viewportCenter = {
              x: window.innerWidth / 2,
              y: window.innerHeight / 2
            };
            
            const flowPosition = reactFlowInstance.screenToFlowPosition(viewportCenter);
            
            // Criar novo nó de imagem
            const newImageNode: Node<NodeData> = {
              id: `image-${Date.now()}`,
              type: 'imageNode',
              position: flowPosition,
              data: {
                imageData,
                uuid: generateUUID(),
                width: 200,
                height: 150
              }
            };
            
            setNodes((nds) => [...nds, newImageNode]);
            toast.success("Imagem colada com sucesso");
          };
          
          reader.onerror = () => {
            toast.error("Erro ao processar a imagem");
          };
          
          reader.readAsDataURL(file);
          break; // Processar apenas a primeira imagem
        }
      }
    };
    
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [reactFlowInstance, setNodes]);

  const onPaneClick = useCallback(() => {
    closeContextMenu();
    clearSelection();
  }, [closeContextMenu, clearSelection]);

  const handleNodeClick = useCallback((event: React.MouseEvent, node: Node<NodeData>) => {
    const isMultiSelect = event.ctrlKey || event.metaKey;
    toggleNodeSelection(node, isMultiSelect);
    
    // Armazenar selectedImage para permitir delete
    if (node.type === 'imageNode') {
      setSelectedImage(node);
    }
    
    // Não abrir painel de propriedades para componentNode, labeledGroup, textNode, actorNode e imageNode
    if (node.type === 'componentNode' || node.type === 'labeledGroup' || node.type === 'textNode' || node.type === 'actorNode' || node.type === 'imageNode') {
      return;
    }
    
    if (!isMultiSelect || hasSingleSelection) {
      const singleSelected = getSingleSelected();
      if (singleSelected?.type === 'node') {
        openNodePanel(singleSelected.element);
      }
    } else {
      closeNodePanel();
      closeEdgePanel();
      closeGroupPanel();
    }
  }, [toggleNodeSelection, hasSingleSelection, getSingleSelected, openNodePanel, closeNodePanel, closeEdgePanel, closeGroupPanel]);

  const handleNodeDoubleClick = useCallback((event: React.MouseEvent, node: Node<NodeData>) => {
    // Abrir formulário específico baseado no tipo do nó
    if (node.type === 'labeledGroup') {
      openGroupPanel(node);
    } else if (node.type === 'textNode') {
      setSelectedText(node);
      setShowTextPanel(true);
    } else if (node.type === 'actorNode') {
      openActorPanel(node);
    }
    // ComponentNode não faz nada no double-click
  }, [openGroupPanel, openActorPanel, setSelectedText, setShowTextPanel]);

  const handleEdgeClick = useCallback((event: React.MouseEvent, edge: Edge) => {
    const isMultiSelect = event.ctrlKey || event.metaKey;
    toggleEdgeSelection(edge, isMultiSelect);
    
    // Clique simples apenas seleciona, não abre painel de propriedades
    if (isMultiSelect && !hasSingleSelection) {
      closeNodePanel();
      closeEdgePanel();
      closeGroupPanel();
    }
  }, [toggleEdgeSelection, hasSingleSelection, closeNodePanel, closeEdgePanel, closeGroupPanel]);

  const handleEdgeDoubleClick = useCallback((event: React.MouseEvent, edge: Edge) => {
    // Double-click abre o painel de propriedades da linha
    openEdgePanel(edge);
  }, [openEdgePanel]);

  const saveNodeProperties = (nodeId: string, title: string, selectedComponentId: string, selectedComponentName: string, selectedOrganizationId: string, selectedOrganizationName: string, codeChanges7Days?: number, codeChanges30Days?: number, deploys7Days?: number) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              title,
              selectedComponentId,
              selectedComponentName,
              selectedOrganizationId,
              selectedOrganizationName,
              codeChanges7Days: codeChanges7Days !== undefined ? codeChanges7Days : node.data.codeChanges7Days,
              codeChanges30Days: codeChanges30Days !== undefined ? codeChanges30Days : node.data.codeChanges30Days,
              deploys7Days: deploys7Days !== undefined ? deploys7Days : node.data.deploys7Days,
            },
          };
        }
        return node;
      })
    );
    closeNodePanel();
  };

  const saveGroupProperties = useCallback((nodeId: string, title: string, backgroundColor?: string) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              title,
              label: title,
              backgroundColor,
            },
          };
        }
        return node;
      })
    );
    closeGroupPanel();
  }, [setNodes, closeGroupPanel]);

  const saveTextProperties = useCallback((nodeId: string, text: string, textAlign: 'left' | 'center' | 'right') => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              text,
              textAlign,
            },
          };
        }
        return node;
      })
    );
    closeTextPropertiesPanel();
  }, [setNodes, closeTextPropertiesPanel]);

  const saveActorProperties = useCallback((nodeId: string, actorText: string, actorColor: string) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              actorText,
              actorColor,
            },
          };
        }
        return node;
      })
    );
    closeActorPanel();
  }, [setNodes, closeActorPanel]);

  const saveEdgeProperties = useCallback((edgeId: string, type: string, label: string, markerStart: boolean, markerEnd: boolean, color: string, lineStyle: string) => {
    setEdges((eds) =>
      eds.map((edge) => {
        if (edge.id === edgeId) {
          const strokeDasharray = lineStyle === 'dashed' ? '5,5' : lineStyle === 'dotted' ? '2,2' : undefined;
          return {
            ...edge,
            type,
            label: label || undefined,
            markerStart: markerStart ? { type: MarkerType.ArrowClosed } : undefined,
            markerEnd: markerEnd ? { type: MarkerType.ArrowClosed } : undefined,
            style: {
              ...edge.style,
              stroke: color,
              strokeDasharray,
            },
          };
        }
        return edge;
      })
    );
  }, [setEdges]);

  const onSave = useCallback(async () => {
    if (reactFlowInstance) {
      const result = await handleSave(nodes, edges);
      if (result.success) {
        markAsSaved();
      }
    } else {
      toast.error("Não foi possível salvar o diagrama");
    }
  }, [nodes, edges, handleSave, reactFlowInstance, markAsSaved]);

  const onUndo = useCallback(() => {
    const result = handleUndo();
    if (result) {
      setNodes(result.newState.nodes);
      setEdges(result.newState.edges);
    }
  }, [handleUndo, setNodes, setEdges]);

  const onRedo = useCallback(() => {
    const result = handleRedo();
    if (result) {
      setNodes(result.newState.nodes);
      setEdges(result.newState.edges);
    }
  }, [handleRedo, setNodes, setEdges]);

  const setReactFlowInstanceCallback = useCallback((instance: any) => {
    setReactFlowInstance(instance);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const debugParam = searchParams.get('debug');
    let debugMode = debugParam === 'true';
    if (!debugParam && location.pathname.includes('&debug=')) {
      debugMode = location.pathname.includes('&debug=true');
    }
    setIsDebugMode(debugMode);
    if (debugMode) toast.info("Modo de debug ativado");
    else if (debugParam === 'false') toast.info("Modo de debug desativado");
  }, [location.search, location.pathname]);

  useEffect(() => {
    const loadComponents = async () => {
      // Só buscar se tiver subscription_id
      if (!subscriptionData?.subscription_id) {
        setComponents([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const data = await fetchSubscriptionComponents(subscriptionData.subscription_id);
        setComponents(data);
      } catch (error) {
        toast.error('Failed to load components');
        setComponents([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadComponents();
  }, [subscriptionData?.subscription_id]);

  // Diagram loading is now handled by useDiagramLoad hook

  const isContextMenuOpen = contextTargetType !== null;

  // Map context target type for the context menu component
  const getContextMenuType = (): "node" | "edge" | "group" | "text" | "actor" | "image" | null => {
    return contextTargetType as "node" | "edge" | "group" | "text" | "actor" | "image" | null;
  };

  const refreshComponents = useCallback(async () => {
    if (!subscriptionData?.subscription_id) {
      setComponents([]);
      return;
    }

    setIsLoading(true);
    try {
      const data = await fetchSubscriptionComponents(subscriptionData.subscription_id);
      setComponents(data);
    } catch (error) {
      toast.error('Failed to refresh components');
      setComponents([]);
    } finally {
      setIsLoading(false);
    }
  }, [subscriptionData?.subscription_id]);

  // Função para abrir o dialog de edição de componente
  const handleEditComponent = useCallback(async () => {
    if (contextTargetNode?.data?.selectedComponentId && contextTargetNode.data.selectedComponentId !== 'novo') {
      try {
        setIsLoading(true);
        // Buscar os dados completos do componente
        const { data: componentData, error } = await supabase
          .from('components')
          .select('*')
          .eq('id', contextTargetNode.data.selectedComponentId)
          .single();

        if (error || !componentData) {
          toast.error('Erro ao carregar dados do componente');
          return;
        }

        setCurrentEditingComponent(componentData as Component);
        setEditComponentDialogOpen(true);
        setContextTargetType(null);
      } catch (error) {
        toast.error('Erro ao carregar componente');
      } finally {
        setIsLoading(false);
      }
    }
  }, [contextTargetNode, setContextTargetType]);

  // Função para ver detalhes do componente
  const handleViewComponentDetails = useCallback(() => {
    if (contextTargetNode?.data?.selectedComponentId && contextTargetNode.data.selectedComponentId !== 'novo') {
      window.open(`/componente-detalhes/${contextTargetNode.data.selectedComponentId}`, '_blank');
    }
    setContextTargetType(null);
  }, [contextTargetNode, setContextTargetType]);

  // Função para salvar alterações do componente
  const handleSaveComponent = useCallback(async (
    name: string, 
    componentTypeId: string, 
    repoType: string, 
    repoName: string, 
    repoUrl: string, 
    mainUrl: string, 
    logsUrl: string, 
    dashUrl: string, 
    swaggerUrl: string
  ) => {
    if (!currentEditingComponent) return;
    
    setIsComponentSaving(true);
    try {
      // Atualizar o componente no banco
      const { error } = await supabase
        .from('components')
        .update({
          name,
          id_component_type: componentTypeId,
          repo_type: repoType,
          repo_name: repoName,
          repo_url: repoUrl,
          main_url: mainUrl,
          logs_url: logsUrl,
          dash_url: dashUrl,
          swagger_url: swaggerUrl,
        })
        .eq('id', currentEditingComponent.id);

      if (error) {
        toast.error('Erro ao salvar componente');
        return;
      }

      // Buscar o nome do tipo de componente atualizado
      const updatedComponentType = componentTypes.find(ct => ct.id === componentTypeId);
      
      // Atualizar todos os nodes do diagrama que referenciam este componente
      setNodes((nds) =>
        nds.map((node) => {
          if (node.data.selectedComponentId === currentEditingComponent.id) {
            return {
              ...node,
              data: {
                ...node.data,
                selectedComponentName: name,
                title: name,
              },
            };
          }
          return node;
        })
      );

      // Recarregar componentes da sidebar
      await refreshComponents();
      
      setEditComponentDialogOpen(false);
      setCurrentEditingComponent(null);
      toast.success('Componente atualizado com sucesso');
    } catch (error) {
      toast.error('Erro ao salvar componente');
    } finally {
      setIsComponentSaving(false);
    }
  }, [currentEditingComponent, componentTypes, setNodes, refreshComponents]);

  // Função para adicionar componente ao canvas automaticamente
  const addNewComponentToCanvas = useCallback((component: Component) => {
    if (!reactFlowInstance) return;

    // Posicionar logo abaixo da barra superior central, na direção do ícone de adicionar
    const viewport = reactFlowInstance.getViewport();
    const belowToolbarPosition = reactFlowInstance.screenToFlowPosition({
      x: window.innerWidth / 2, // Centro horizontal da tela
      y: 150, // Logo abaixo da barra de ferramentas superior
    });

    const newNode: Node<NodeData> = {
      id: `component-${Date.now()}`,
      type: 'componentNode',
      position: belowToolbarPosition,
      data: {
        name: component.component_types?.name || 'Componente',
        componentTypeId: component.id_component_type,
        title: component.name,
        uuid: generateUUID(),
        selectedComponentId: component.id,
        selectedComponentName: component.name,
        selectedOrganizationId: component.id_organization,
        selectedOrganizationName: (component as any).organizations?.name || '',
        codeChanges7Days: component.code_changes_7days || 0,
        codeChanges30Days: component.code_changes_7days || 0,
        deploys7Days: component.deploys_7days || 0,
        icon: component.component_types?.icon,
      },
    };

    setNodes(nodes => [...nodes, newNode]);
    toast.success('Componente adicionado ao diagrama');
  }, [reactFlowInstance, setNodes]);

  // Função para criar novo componente
  const handleCreateComponent = useCallback(async (name: string, componentTypeId: string, repoType: string, repoName: string, repoUrl: string, mainUrl: string, logsUrl: string, dashUrl: string, swaggerUrl: string) => {
    if (!currentOrganization?.id) {
      toast.error("Nenhuma organização selecionada");
      return;
    }
    
    setIsSaving(true);
    try {
      const result = await createComponent(name, componentTypeId, currentOrganization.id, repoType, repoName, repoUrl, mainUrl, logsUrl, dashUrl, swaggerUrl);
      
      if (result.success && result.component) {
        // Recarregar componentes
        await refreshComponents();
        
        // Fechar dialog
        setCreateComponentDialogOpen(false);
        
        // Buscar o componente criado com dados completos
        const { data: componentData, error } = await supabase
          .from('components')
          .select(`
            *,
            component_types (
              id,
              name,
              icon,
              group_name
            ),
            organizations (
              id,
              name
            )
          `)
          .eq('id', result.component.id)
          .single();

        if (componentData && !error) {
          // Adicionar ao canvas automaticamente
          addNewComponentToCanvas(componentData as Component);
        }
        
        toast.success("Componente criado com sucesso!");
      }
    } catch (error) {
      console.error("Erro ao criar componente:", error);
      toast.error("Erro ao criar componente");
    } finally {
      setIsSaving(false);
    }
  }, [currentOrganization, createComponent, refreshComponents, addNewComponentToCanvas]);

  // Função para criar novo tipo de componente
  const handleCreateComponentType = useCallback(async (name: string, icon: string, groupName: string) => {
    setIsSaving(true);
    try {
      const result = await createComponentType(name, icon, groupName, subscriptionData?.subscription_id);
      
      if (result) {
        await refreshComponents();
        setCreateTypeDialogOpen(false);
        toast.success("Tipo de componente criado com sucesso!");
      }
    } catch (error) {
      console.error("Erro ao criar tipo de componente:", error);
      toast.error("Erro ao criar tipo de componente");
    } finally {
      setIsSaving(false);
    }
  }, [createComponentType, subscriptionData?.subscription_id, refreshComponents]);

  // Função para abrir dialog de novo componente
  const handleAddNew = useCallback(async () => {
    // Carregar tipos de componentes se necessário
    try {
      await fetchComponentTypes(subscriptionData?.subscription_id);
    } catch (error) {
      console.error("Erro ao carregar tipos de componentes:", error);
    }
    setCreateComponentDialogOpen(true);
  }, [fetchComponentTypes, subscriptionData?.subscription_id]);

  return (
    <ReactFlowProvider>
      <div className="h-full w-full flex flex-col">
        <DiagramEditorArea
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          setNodes={setNodes}
          setEdges={setEdges}
          onNodeContextMenu={handleContextMenuNode}
          onEdgeContextMenu={handleContextMenuEdge}
          onPaneClick={onPaneClick}
          components={components}
          isLoading={isLoading}
          diagramName={diagramName}
          setReactFlowInstance={setReactFlowInstanceCallback}
          onSave={onSave}
          onUndo={onUndo}
          onRedo={onRedo}
          canUndo={canUndo}
          canRedo={canRedo}
          subscriptionId={subscriptionData?.subscription_id}
          refreshComponents={refreshComponents}
          onAddNew={handleAddNew}
          selectedElements={selectedElements}
          onElementsSelect={setMultipleSelection}
          onNodeClick={handleNodeClick}
          onEdgeClick={handleEdgeClick}
          onNodeDoubleClick={handleNodeDoubleClick}
          onEdgeDoubleClick={handleEdgeDoubleClick}
          autoSaveStatus={autoSaveStatus}
        />
        {isContextMenuOpen && (
          <DiagramContextMenu
            position={contextMenuPosition}
            type={getContextMenuType()}
            nodeData={contextTargetNode?.data}
            onEdit={() => {
              if (contextTargetType === 'node') openNodePropertiesPanel();
              else if (contextTargetType === 'edge') openEdgePropertiesPanel();
              else if (contextTargetType === 'group') openGroupPropertiesPanel();
              else if (contextTargetType === 'text') openTextPropertiesPanel();
              else if (contextTargetType === 'actor') openActorPropertiesPanel();
            }}
            onEditComponent={handleEditComponent}
            onViewComponentDetails={handleViewComponentDetails}
            onDelete={handleDeleteContextTarget}
            onSendToBack={handleSendToBack}
            onBringToFront={handleBringToFront}
            onClose={() => setContextTargetType(null)}
          />
        )}
        <DiagramPropertiesPanels
          selectedNode={showNodePanel && selectedNode ? selectedNode : null}
          closePropertiesPanel={closeNodePanel}
          saveNodeProperties={saveNodeProperties}
          selectedEdge={showEdgePanel && selectedEdge ? selectedEdge : null}
          closeEdgePropertiesPanel={closeEdgePanel}
          saveEdgeProperties={saveEdgeProperties}
          selectedGroup={showGroupPanel && selectedGroup ? selectedGroup : null}
          closeGroupPropertiesPanel={closeGroupPanel}
          saveGroupProperties={saveGroupProperties}
          selectedText={showTextPanel && selectedText ? selectedText : null}
          closeTextPropertiesPanel={closeTextPropertiesPanel}
          saveTextProperties={saveTextProperties}
          selectedActor={showActorPanel && selectedActor ? selectedActor : null}
          closeActorPropertiesPanel={closeActorPanel}
          saveActorProperties={saveActorProperties}
        />
        <EditComponentDialog
          open={editComponentDialogOpen}
          onOpenChange={setEditComponentDialogOpen}
          component={currentEditingComponent}
          onSave={handleSaveComponent}
          componentTypes={componentTypes}
          isLoading={isComponentSaving}
        />
        
        {/* Dialogs para criação de componentes */}
        <CreateComponentDialog
          open={createComponentDialogOpen}
          onOpenChange={setCreateComponentDialogOpen}
          onSave={handleCreateComponent}
          componentTypes={componentTypes}
          isLoading={isSaving}
        />
        
        <ComponentTypeDialog
          open={createTypeDialogOpen}
          onOpenChange={setCreateTypeDialogOpen}
          onSave={handleCreateComponentType}
          componentType={null}
          isLoading={isSaving}
          mode="create"
        />
      </div>
    </ReactFlowProvider>
  );
}
